import React, { useEffect, useState, useContext } from "react";
import {
  Table,
  Spinner,
  Alert,
  Container,
  Card,
  Button,
  Modal,
} from "react-bootstrap";
import { useNavigate, Link } from "react-router-dom";
import { ThemeContext } from "contexts/ThemeContext.jsx";
import { serverHost } from "variables/serverdata";
import { sortRows } from "../util/helpers";
import { InputGroup, InputGroupText, Input, FormGroup, Form } from "reactstrap";
import { hasPermission, PERMISSIONS } from "utils/permissions";
import HusariaHeader from "views/pages/components/HusariaHeader";
import paginationFactory from "react-bootstrap-table2-paginator";
import ToolkitProvider from "react-bootstrap-table2-toolkit/dist/react-bootstrap-table2-toolkit";
import BootstrapTable from "react-bootstrap-table-next";


const DeviceVendorsTable = () => {
  const { theme } = useContext(ThemeContext);
  const isDark = theme === "dark";
  const userData = React.useMemo(() => {
    const storedUser = localStorage.getItem("user");
    return storedUser ? JSON.parse(storedUser) : null;
  }, []);
  const canEdit = hasPermission(userData, PERMISSIONS.EDIT);
  const canDelete = hasPermission(userData, PERMISSIONS.DELETE);
  const canPerformActions = canEdit || canDelete;
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleteModal, setDeleteModal] = useState(false);
  const [vendorToDelete, setVendorToDelete] = useState(null);
  const navigate = useNavigate();
  const [search, setSearch] = useState("");

  const visibleColumns = [
    { key: "name", label: "Vendor Name" },
    { key: "enabled", label: "Enabled" },
    { key: "description", label: "Description" },
  ];

  const booleanColumns = ["enabled"];

  const formatBoolean = (val) =>
    val === true || val === "t" || val === "true" ? "Yes" : "No";

  const columns = visibleColumns.map((col, index) => ({
    dataField: col.key,
    text: col.label,
    sort: true,
    formatter: (cell, row) => {
      if (booleanColumns.includes(col.key)) {
        return formatBoolean(cell);
      }

      if (index === 0) {
        return (
          <span
            className="tr-row-name"
            style={{ cursor: canEdit ? "pointer" : "default" }}
            onClick={() => {
              if (canEdit) {
                navigate(
                  `/admin/hucs/edit/deviceVendor/${row.device_vendor_uuid}`
                );
              }
            }}
          >
            {cell ?? "-"}
          </span>
        );
      }

      return cell ?? "-";
    },
  }));

  if (canPerformActions) {
    columns.push({
      dataField: "actions",
      text: "Actions",
      formatter: (_, row) =>
        canDelete ? (
          <Button
            size="sm"
            variant="danger"
            onClick={(e) => {
              e.stopPropagation();
              toggleDeleteModal(row);
            }}
          >
            Delete
          </Button>
        ) : null,
    });
  }

  const pagination = paginationFactory({
    page: 1,
    alwaysShowAllBtns: true,
    showTotal: true,
    withFirstAndLast: false,
    sizePerPageRenderer: ({ onSizePerPageChange }) => (
      <div className="dataTables_length">
        <label>
          Show{" "}
          <select
            className="form-control form-control-sm"
            onChange={(e) => onSizePerPageChange(e.target.value)}
          >
            <option value="10">10</option>
            <option value="25">25</option>
            <option value="50">50</option>
            <option value="100">100</option>
          </select>{" "}
          entries.
        </label>
      </div>
    ),
  });

  const fetchVendors = async () => {
    setLoading(true);
    try {
      const res = await fetch(
        `${serverHost}/get_rows?table=v_device_vendors&pageSize=50&page=1`
      );
      if (!res.ok) throw new Error("Failed to load vendors");
      const data = await res.json();
      setVendors(data.results || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVendors();
  }, []);

  const toggleDeleteModal = (vendor = null) => {
    setVendorToDelete(vendor);
    setDeleteModal(!deleteModal);
  };

  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: "asc",
  });

  const handleSort = (key) => {
    setSortConfig((prev) => {
      if (prev.key === key) {
        return {
          key,
          direction: prev.direction === "asc" ? "desc" : "asc",
        };
      }
      return { key, direction: "asc" };
    });
  };

  const filteredVendors = React.useMemo(() => {
    if (!search) return vendors;

    return vendors.filter((row) =>
      visibleColumns.some((col) =>
        String(row[col.key] ?? "")
          .toLowerCase()
          .includes(search.toLowerCase())
      )
    );
  }, [vendors, search, visibleColumns]);

  const handleDelete = async () => {
    if (!vendorToDelete) return;

    try {
      const res = await fetch(
        `${serverHost}/delete_device_vendor/${vendorToDelete.device_vendor_uuid}`,
        { method: "DELETE" }
      );
      if (!res.ok) throw new Error("Delete failed");
      await res.json();
      toggleDeleteModal();
      fetchVendors();
    } catch (err) {
      alert(`Error deleting vendor: ${err.message}`);
    }
  };

  if (loading)
    return (
      <div className="text-center mt-5">
        <Spinner animation="border" variant={isDark ? "light" : "primary"} />
        <p className="mt-2" style={{ color: isDark ? "#e2e8f0" : "#212529" }}>
          Loading vendors...
        </p>
      </div>
    );

  if (error)
    return (
      <Alert variant="danger" className="mt-5 text-center">
        {error}
      </Alert>
    );

  return (
    <>
      <HusariaHeader name="Device Vendors" parentName="HUCS" padding="4.5rem" />

      <Container
        fluid
        className={`mt--6 ${isDark ? "table-dark-mode" : ""}`}
      >
        <Card className={`shadow-sm ${isDark ? "bg-dark text-light" : ""}`}>
          <Card.Header
            style={{
              backgroundColor: isDark ? "#1f2937" : "#f0f0f0",
              color: isDark ? "#e2e8f0" : "#111",
            }}
            className="d-flex justify-content-between align-items-center"
          >
            {/* LEFT: Title + Search */}
            <div className="d-flex align-items-center">

              <Form className="navbar-search navbar-search-light form-inline">
                <FormGroup className="mb-0">
                  <InputGroup className="input-group-alternative input-group-merge search-border">
                    <InputGroupText>
                      <i className="fas fa-search search-icon" />
                    </InputGroupText>

                    <Input
                      className="form-control table-search-bar"
                      placeholder="Search vendors"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      style={{ width: "220px" }}
                    />
                  </InputGroup>
                </FormGroup>
              </Form>
            </div>

            {/* RIGHT: Actions */}
            <Button
              variant="primary"
              size="sm"
              onClick={() => navigate("/admin/hucs/add/deviceVendor")}
            >
              + Add Vendor
            </Button>
          </Card.Header>

          <Card.Body
            style={{ padding: 0, backgroundColor: isDark ? "#1f2937" : "#fff" }}
          >
            <div style={{ overflowX: "auto" }}>
              <ToolkitProvider
                keyField="device_vendor_uuid"
                data={filteredVendors}
                columns={columns}
                search
              >
                {(props) => (
                  <div className="py-4 table-responsive">
                    <BootstrapTable
                      {...props.baseProps}
                      bootstrap4
                      pagination={pagination}
                      bordered={false}
                      hover
                      rowStyle={{ cursor: "pointer" }}
                      classes="align-items-center table-flush"
                      rowEvents={{
                        onClick: (_, row) => {
                          if (canEdit) {
                            navigate(
                              `/admin/hucs/edit/deviceVendor/${row.device_vendor_uuid}`
                            );
                          }
                        },
                      }}
                      noDataIndication={() =>
                        loading ? (
                          <div className="text-center">Loading...</div>
                        ) : (
                          <div className="text-center">No vendors found.</div>
                        )
                      }
                    />
                  </div>
                )}
              </ToolkitProvider>
            </div>
          </Card.Body>
        </Card>
      </Container>

      <Modal show={deleteModal} onHide={toggleDeleteModal}>
        <Modal.Header
          closeButton
          style={{
            backgroundColor: isDark ? "#1f2937" : "#f8f9fa",
            color: isDark ? "#e2e8f0" : "#212529",
          }}
        >
          <Modal.Title
            style={{ color: isDark ? "#e2e8f0" : "#212529" }}
          >
            Delete Vendor
          </Modal.Title>
        </Modal.Header>

        <Modal.Body
          style={{
            backgroundColor: isDark ? "#111827" : "#fff",
            color: isDark ? "#e2e8f0" : "#212529",
          }}
        >
          Are you sure you want to delete this vendor? 
          <br />
          <strong>This action cannot be undone.</strong>
        </Modal.Body>

        <Modal.Footer
          style={{
            backgroundColor: isDark ? "#1f2937" : "#f8f9fa",
          }}
        >
          <Button variant="light" onClick={toggleDeleteModal}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDelete}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default DeviceVendorsTable;
